import { useCallback, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CAPTURE_PRIMARY_ACTIONS } from '@packproof/ux';
import { createMobileHandoff, getTransaction, type PortalMobileHandoff, type PortalTransaction } from '../api';
import { revisionsOf, workspaceOf } from '../workspace';
import { useAuth } from '../auth';
import { QrPanel } from '../QrPanel';
import { usePortalWorkspaceRefresh } from '../workspace-refresh';

export function HandoffPage() {
  const { id = '' } = useParams();
  const { user } = useAuth();
  const [item, setItem] = useState<PortalTransaction | null>(null);
  const [handoff, setHandoff] = useState<PortalMobileHandoff | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mintedAction = useRef<string | null>(null);

  const load = useCallback(() => {
    if (!id || !user) return Promise.resolve();
    return getTransaction(id)
      .then(async (result) => {
        setItem(result.data);
        setError(null);
        const next = workspaceOf(result.data).nextAction;
        const action = next.primaryAction?.kind;
        if (!action || !CAPTURE_PRIMARY_ACTIONS.has(action)) {
          mintedAction.current = null;
          setHandoff(null);
          setMessage(next.headline || 'Nothing needs phone capture on this PackProof right now.');
          return;
        }
        setMessage(null);
        if (mintedAction.current === action) return;
        const minted = await createMobileHandoff(result.data.id, action);
        mintedAction.current = action;
        setHandoff(minted.data);
      })
      .catch((caught: unknown) => {
        setError(caught instanceof Error ? caught.message : 'Could not start phone capture.');
      });
  }, [id, user]);

  usePortalWorkspaceRefresh(load, {
    processingState: item ? workspaceOf(item).evidenceProcessing.state : 'IDLE',
    revisions: item ? revisionsOf([item]) : [],
    transactionIds: id ? [id] : [],
  });

  return (
    <>
      <p className="eyebrow">Phone capture</p>
      <h1>Continue on your phone</h1>
      <p className="lede">The browser is the workspace. Your phone performs trusted physical capture. Scan the code or open the app to continue this exact PackProof.</p>
      {error ? <p className="error">{error}</p> : null}
      {message ? (
        <article className="card">
          <h2>{item?.title ?? 'PackProof'}</h2>
          <p>{message}</p>
          {item ? <Link className="btn secondary" to={`/packproofs/${item.id}`}>Open workspace</Link> : null}
        </article>
      ) : null}
      {handoff ? (
        <article className="card qr">
          <QrPanel value={handoff.universalLink} />
          <div>
            <h2>{item?.title ?? 'PackProof'}</h2>
            <p>Already have PackProof installed?</p>
            <div className="row">
              <a className="btn" href={handoff.appLink}>Open PackProof</a>
              <a className="btn secondary" href={handoff.storeUrl} rel="noreferrer">Get the app</a>
            </div>
            <p className="hash">{handoff.universalLink}</p>
          </div>
        </article>
      ) : null}
      {!handoff && !message && !error ? <p className="meta">Preparing a phone handoff…</p> : null}
    </>
  );
}
